/* eslint-disable max-classes-per-file */
import { useState } from "react";
import { GroupDescriptor } from "./group-by";
import TicketItem from "./ticket-item";
import { SortBy } from "./sort-by/base";
import BaseListener from "./base-listener";
import TicketColumn from "./ticket-column";
import TicketColumnState from "./ticket-column-state";

type DisplayState = [boolean, (value: boolean) => void];

export default class TicketColumnGroup {
    private displayState?: DisplayState;

    private _tickets: TicketItem[] = [];

    private _listener = new BaseListener();

    private isSorted = false;

    // eslint-disable-next-line no-useless-constructor
    public constructor(
        public readonly group: GroupDescriptor | undefined,
        private sorter: SortBy | undefined,
        private column?: TicketColumn,
        private columnState?: TicketColumnState,
    ) {
        // nope
    }

    public get tickets() {
        if (!this.isSorted && this.sorter) {
            this._tickets = this.sorter.sort(this._tickets);
            this.isSorted = true;
        }
        return this._tickets;
    }

    public get length() {
        return this._tickets.length;
    }

    public setSorter(sorter: SortBy | undefined) {
        this.sorter = sorter;
        this.isSorted = false;
    }

    public clear() {
        this.isSorted = false;
        this._tickets = [];
    }

    public addTicket(ticket: TicketItem) {
        this.isSorted = false;
        this._tickets?.push(ticket);
        ticket.setGroup(this);
    }

    public removeTicket(ticket: TicketItem) {
        this.isSorted = false;
        const index = this._tickets.findIndex(item => item.id === ticket.id);
        if (index >= 0) {
            this._tickets.splice(index, 1);
        }
    }

    public useDisplay() {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        this._listener.useListener();
    }

    public update() {
        this._listener.updateListeners();
    }

    public useDisplayState(): DisplayState {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        this.displayState = useState(this.isExpanded);
        return [this.displayState[0], (value: boolean) => this.setIsVisible(value)];
    }

    public get listener() {
        return this._listener;
    }

    public expand() {
        this.setIsVisible(true);
    }

    public collapse() {
        this.setIsVisible(false);
    }

    private get columnId() {
        return this.column?.ticketState.id;
    }

    private get groupId() {
        return this.group?.id ?? "default";
    }

    public setIsVisible(isVisible: boolean) {
        this.displayState?.[1]?.(isVisible);

        if (isVisible) {
            this.columnState?.expand(this.columnId, this.groupId);
        } else {
            this.columnState?.collapse(this.columnId, this.groupId);
        }

        this._listener.updateListeners();
    }

    public get isExpanded() {
        return !this.isCollapsed;
    }

    public get isCollapsed() {
        if (!this.columnState) {
            return false;
        }
        return this.columnState?.isCollapsed(this.columnId, this.groupId);
    }
}
